import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useState } from 'react'
import { MdAdd } from 'react-icons/md'
import ReactModal from 'react-modal'
import ReactPlayer from 'react-player'

import type { ChangeEvent, ReactElement } from 'react'
import type { IVideoResultItem } from '~/types/video'

import CreateModal from './Modals/CreateModal'
import UpdateModal from './Modals/UpdateModal'
import { keys, useUserVideosListQuery, useVideosCategoriesListQuery, useVideosTagsListQuery } from './queries'
import VideosByCategory from './VideosByCategory'

import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import Filters, { FiltersContent } from '~/components/Filters'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { VIDEO_CATEGORIES } from '~/types/video'
import { debounce } from '~/utils/function'

import './styles.scss'

const Videos = (): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)

  const [videoToUpdate, setVideoToUpdate] = useState<IVideoResultItem>()

  const [search, setSearch] = useState('')

  const [isPlayerOpen, setIsPlayerOpen] = useState(false)

  const [activeVideoUrl, setActiveVideoUrl] = useState<string>()

  /** Queries & Mutations */

  const { data = [], isLoading } = useVideosCategoriesListQuery()

  const { data: userVideos } = useUserVideosListQuery({ search })

  useVideosTagsListQuery()

  /** Effects */

  useDocumentTitle('Videos')

  /** Handlers */

  const debouncedSearch = debounce((search) => setSearch(search), 500)

  const handleSearchChanged = (e: ChangeEvent<HTMLInputElement>) => debouncedSearch(e.target.value)

  const handleToggleCreateModal = (isOpen: boolean) => () => setIsCreateModalOpen(isOpen)

  const refetchQueries = () => queryClient.invalidateQueries({ queryKey: keys.all })

  const handleToggleUpdateModal = (isOpen: boolean) => () => setIsUpdateModalOpen(isOpen)

  const handleEditVideo = (video: IVideoResultItem) => {
    setVideoToUpdate(video)
    setIsUpdateModalOpen(true)
  }

  const handleThumbnailClick = (videoUrl: string) => {
    setActiveVideoUrl(videoUrl)
    setIsPlayerOpen(true)
  }

  const handleClosePlayer = () => {
    setIsPlayerOpen(false)
    setActiveVideoUrl(undefined)
  }

  /** Render */

  return (
    <>
      <ViewTitle className="layout-view-title top-zero">Videos</ViewTitle>
      <Filters className="videos-filters">
        <FiltersContent>
          <SearchInput className="table-search-bar" disabled={isLoading} onChange={handleSearchChanged} />
          <Button className="new-video-button" onClick={handleToggleCreateModal(true)}>
            <MdAdd color="#fff" size={16} />
            New video
          </Button>
        </FiltersContent>
      </Filters>
      <Layout className="videos-view" withPadding>
        <ContentLayout className={classNames({ 'is-loading': isLoading })}>
          <Section className="section">
            <div className="videos-container">
              <VideosByCategory
                category={VIDEO_CATEGORIES.MY_LIST}
                favouriteVideos={userVideos?.data}
                onEdit={handleEditVideo}
                onThumbnailClick={handleThumbnailClick}
                search={search}
                title="My list"
              />
              <VideosByCategory
                category={VIDEO_CATEGORIES.LATEST}
                favouriteVideos={userVideos?.data}
                onEdit={handleEditVideo}
                onThumbnailClick={handleThumbnailClick}
                search={search}
                title="Latest videos"
              />
              {data.map(({ label, value }) => (
                <VideosByCategory
                  category={value}
                  favouriteVideos={userVideos?.data}
                  key={value}
                  onEdit={handleEditVideo}
                  onThumbnailClick={handleThumbnailClick}
                  search={search}
                  title={label}
                />
              ))}
            </div>
          </Section>
        </ContentLayout>
        <CreateModal isOpen={isCreateModalOpen} onClose={handleToggleCreateModal(false)} onSubmit={refetchQueries} />
        {videoToUpdate ? (
          <UpdateModal
            isOpen={isUpdateModalOpen}
            onClose={handleToggleUpdateModal(false)}
            onSubmit={refetchQueries}
            video={videoToUpdate}
          />
        ) : null}
        <ReactModal
          className="video-player-modal"
          isOpen={isPlayerOpen}
          onRequestClose={handleClosePlayer}
          overlayClassName="video-player-overlay"
          shouldCloseOnOverlayClick
        >
          <ReactPlayer controls height="100%" playing url={activeVideoUrl} width="100%" />
        </ReactModal>
      </Layout>
    </>
  )
}

export default Videos
